/**
 * The V2 lane's message catalogue: every socket message type PlayChess knows
 * about, mapped to the schemapack schema the SDK must encode/decode it with.
 *
 * The (type -> schema) pairs are taken verbatim from PlayChess's
 * `CCApplicationClient()` registration block (src/Screens/PlayChess/index.js
 * L1180-1751) — the parity test in `__tests__/parity/handlerParity.test.ts`
 * re-reads that file and fails the build if it ever grows a type this map
 * lacks.
 *
 * Splitting rule used here:
 *   INBOUND  = every type PlayChess registers a `PlayChess:<type>` handler for
 *              (37 types), plus `invalid_move` which the server sends but
 *              PlayChess never handled.
 *   OUTBOUND = every type we (or PlayChess) *send*, i.e. the registerMessageType
 *              calls with no handler plus the request types that also carry an
 *              echo handler, plus the 4 arena request types.
 * A few request types appear in both maps: the server echoes them back and
 * PlayChess registered a (no-op) handler for the echo.
 *
 * NOTE the SDK is the same shim SocketProvider/PlayChess use (`CCClientSdk.ts`
 * at the repo root) so there is exactly one schema registry in the bundle.
 */
const {schema} = require('../../../CCClientSdk');

/**
 * `invalid_move` has NO schema in cc-client-sdk (node_modules/cc-client-sdk/
 * lib/schema.js exports no `invalidMoveMsgSchema`), so it is defined inline
 * here with the 7 fields cc-game-service sends
 * (common/gameEventHandler.js). Once the SDK ships one (via the
 * `cc-add-message-type` workflow) this constant should be replaced by
 * `schema.invalidMoveMsgSchema`.
 */
export const invalidMoveMsgSchema = schema.invalidMoveMsgSchema || {
  requestId: 'string',
  type: 'string',
  move: 'string',
  userId: 'string',
  data: 'string',
  tableId: 'uint32',
  message: 'string',
};

/**
 * Arena rides ONE envelope schema for all 7 message types. Guarded exactly
 * like PlayChess L1716-1751: an older installed cc-client-sdk must not break
 * the rest of the gameplay surface.
 */
export const arenaSupported: boolean = Boolean(schema.arenaEnvelopeSchema);

export const ARENA_INBOUND_TYPES = [
  'arena_status_response',
  'arena_standings_response',
  'arena_berserk_response',
] as const;

export const ARENA_OUTBOUND_TYPES = [
  'arena_resume_request',
  'arena_pause_request',
  'arena_status_request',
  'arena_berserk_request',
] as const;

export type MessageFamily =
  | 'game'
  | 'challenge'
  | 'tournament'
  | 'arena'
  | 'watch';

type SchemaMap = {[type: string]: any};

const gameInbound: SchemaMap = {
  start_game_response: schema.startGameResMsgSchema,
  join_game_response: schema.joinGameResMsgSchema,
  game_ready_response: schema.gameReadyResMsgSchema,
  start_game: schema.startGameMsgSchema,
  player_turn_response: schema.eventResMsgSchema,
  player_move: schema.moveResMsgSchema,
  invalid_move: invalidMoveMsgSchema,
  draw_check: schema.drawCheckMsgSchema,
  draw_reject: schema.drawRejectMsgSchema,
  game_end_response: schema.gameEndResMsgSchema,
  no_ongoing_game: schema.emptyGameStateSchema,
  tournament_opening_prep_eval_pending_response:
    schema.tournamentOpeningPrepEvalPendingResMsgSchema,
  // echo of our own join/leave requests (PlayChess registers no-op handlers)
  join_game_request: schema.joinGameReqMsgSchema,
  leave_table_request: schema.leaveTableReqMsgSchema,
  fetch_game_state: schema.fetchGameStateMsgSchema,
};

// All five challenge_* types share ONE schema — every field must be present on
// the wire or schemapack drops it (protocol contract).
const challengeInbound: SchemaMap = {
  challenge_cancel: schema.challengeGameMsgSchema,
  challenge_reject: schema.challengeGameMsgSchema,
  challenge_request_expire: schema.challengeGameMsgSchema,
  challenge_game_request: schema.challengeGameMsgSchema,
  online_players_search: schema.onlinePlayersSearchMsgSchema,
  online_players_result: schema.onlinePlayersResultMsgSchema,
};

const tournamentInbound: SchemaMap = {
  join_tournament_response: schema.joinTournamentResMsgSchema,
  // Deliberately schema-LESS (plain JSON): the overlap-guard detail message
  // carries keys the fixed 8-field join_tournament_response schema would drop.
  // PlayChess L1500-1506 registers the handler only — mirror that by mapping
  // it to `undefined` so registration skips the schema.
  join_tournament_conflict: undefined,
  withdraw_tournament_response: schema.withDrawTournamentResMsgSchema,
  tournament_end_response: schema.tournamentEndResMegSchema, // (sic) SDK typo
  tournament_status_response: schema.tournamentPairingBroadcastSchema,
  pairing: schema.tournamentPairingBroadcastSchema,
  tournament_game_end_response: schema.tournamentPairingBroadcastSchema,
  // NOTE: `nextRoundTimerResMsgSchema` exists only from cc-client-sdk 2.2.1
  // (the Swiss round-break build). On an older install this is `undefined`,
  // i.e. schema-less — exactly what the old PlayChess registers on the same
  // install. cc-game-service schemapack-ENCODES this type (app.js
  // registerSchemaPack), so a client that wants the payload decoded must be on
  // SDK >= 2.2.1; the mapping resolves to the real schema automatically then.
  next_round_timer_response: schema.nextRoundTimerResMsgSchema,
  tournament_starting_soon: schema.tournamentStartingSchema,
  bye_response: schema.tournamentByeMsgSchema,
};

const watchInbound: SchemaMap = {
  watch_tournament_game_response: schema.watchTournamentGameResMsgSchema,
  watch_player_turn_response: schema.watchPlayerTurnResMsgSchema,
  watch_game_end_response: schema.gameEndResMsgSchema,
  stop_watching_tournament_game_response:
    schema.stopWatchingTournamentGameResMsgSchema,
};

const arenaInbound: SchemaMap = arenaSupported
  ? ARENA_INBOUND_TYPES.reduce((acc: SchemaMap, type) => {
      acc[type] = schema.arenaEnvelopeSchema;
      return acc;
    }, {})
  : {};

const gameOutbound: SchemaMap = {
  join_game_request: schema.joinGameReqMsgSchema,
  leave_table_request: schema.leaveTableReqMsgSchema,
  // player_move carries the move AND resign/abort/draw/touch_piece variants
  player_move: schema.moveResMsgSchema,
  fetch_game_state: schema.fetchGameStateMsgSchema,
  end_game_confirmed: schema.endGameConfirmSchema,
};

const challengeOutbound: SchemaMap = {
  challenge_game_request: schema.challengeGameMsgSchema,
  online_players_search: schema.onlinePlayersSearchMsgSchema,
};

const tournamentOutbound: SchemaMap = {
  join_tournament_request: schema.joinTournamentReqMsgSchema,
  withdraw_tournament_request: schema.withDrawTournamentReqMsgSchema,
  get_tournament_status: schema.getTournamentStatusMsgSchema,
  stop_watching_tournament_request: schema.stopWatchingTournamentMsgSchema,
};

const watchOutbound: SchemaMap = {
  watch_tournament_game_request: schema.watchTournamentGameReqMsgSchema,
  stop_watching_tournament_game_request:
    schema.stopWatchingTournamentGameReqMsgSchema,
};

const arenaOutbound: SchemaMap = arenaSupported
  ? ARENA_OUTBOUND_TYPES.reduce((acc: SchemaMap, type) => {
      acc[type] = schema.arenaEnvelopeSchema;
      return acc;
    }, {})
  : {};

export const INBOUND: SchemaMap = {
  ...gameInbound,
  ...challengeInbound,
  ...tournamentInbound,
  ...watchInbound,
  ...arenaInbound,
};

export const OUTBOUND: SchemaMap = {
  ...gameOutbound,
  ...challengeOutbound,
  ...tournamentOutbound,
  ...watchOutbound,
  ...arenaOutbound,
};

/** type -> family, for logging/routing and the reducer table. */
export const FAMILY_OF: {[type: string]: MessageFamily} = {
  ...mapFamily(gameInbound, 'game'),
  ...mapFamily(gameOutbound, 'game'),
  ...mapFamily(challengeInbound, 'challenge'),
  ...mapFamily(challengeOutbound, 'challenge'),
  ...mapFamily(tournamentInbound, 'tournament'),
  ...mapFamily(tournamentOutbound, 'tournament'),
  ...mapFamily(watchInbound, 'watch'),
  ...mapFamily(watchOutbound, 'watch'),
  ...mapFamily(arenaInbound, 'arena'),
  ...mapFamily(arenaOutbound, 'arena'),
};

function mapFamily(
  map: SchemaMap,
  family: MessageFamily,
): {[type: string]: MessageFamily} {
  return Object.keys(map).reduce(
    (acc: {[type: string]: MessageFamily}, type) => {
      acc[type] = family;
      return acc;
    },
    {},
  );
}

/**
 * Arena payloads travel as a JSON string on the shared envelope. The parser
 * itself is a pure helper (`chess/arenaEnvelope.ts`) so the reducers can take
 * it without importing this module's SDK `require`; re-exported here because
 * that is where the envelope is documented and registered.
 */
export {parseArenaPayload} from './chess/arenaEnvelope';

// ── Hand-written payload types, mirroring node_modules/cc-client-sdk/lib/
// schema.js. Numeric wire fields decode as numbers, ids are compared as
// strings everywhere (the server mixes uint32 and string for the same id).

/** startGameResMsgSchema (schema.js L267-304) */
export type StartGameResponse = {
  requestId: string;
  type: string;
  message: string;
  playerList: string[]; // "id:name"
  tableId: number;
  tossWinner: number; // WHITE id (authoritative)
  playerMoveTimer: number; // ms
  moveTimeIncrement: number; // ms
  startFen: string;
  opponentuserId: number;
  opponentusername: string;
  rating: number;
  opponentRating: number;
  gamePlayMode: string;
  username: string;
  touchPiece: string; // JSON
  tournamentId: string;
  round: number;
  state: number;
  success: boolean;
};

/** joinGameResMsgSchema (schema.js L306) */
export type JoinGameResponse = {
  requestId: string;
  type: string;
  tableId: number;
  playerList: string[];
  state: number;
};

/** gameReadyResMsgSchema (schema.js L433-447) */
export type GameReadyResponse = {
  requestId: string;
  type: string;
  userId: string;
  tableId: number;
  data: string; // start FEN
  opponentuserId: string;
  opponentusername: string;
  moveTimer: number; // SECONDS on the wire
  username: string;
  customRulesMap: string; // JSON
  userRating: number;
  opponentRating: number;
  tossWinner: number;
};

/** startGameMsgSchema (schema.js L378) */
export type StartGameMessage = {
  requestId: string;
  type: string;
  tableId: number;
};

/** eventResMsgSchema — player_turn_response (schema.js L349-361) */
export type PlayerTurnResponse = {
  requestId: string; // 'player move' | 'on connect' | 'requestid' | 'time_up'
  type: string;
  userId: string; // who moves NEXT
  tableId: number;
  data: string; // new FEN
  whiteTime: number; // SECONDS
  blackTime: number; // SECONDS
  moves: string[]; // DELTA, except 'on connect' (full) and game start ([])
  whitePiecesCaptured: string[]; // always [] — derived from the FEN instead
  blackPiecesCaptured: string[];
  previousFen: string;
};

/** moveResMsgSchema (schema.js L385) */
export type PlayerMoveMessage = {
  requestId: string;
  type: string;
  username: string;
  tableId: number;
  move: string;
};

/** the inline invalid_move schema above */
export type InvalidMoveMessage = {
  requestId: string;
  type: string;
  move: string;
  userId: string;
  data: string; // previous FEN — the board must revert to this
  tableId: number;
  message: string;
};

/** drawCheckMsgSchema (schema.js L393) */
export type DrawCheckMessage = {
  requestId: string;
  type: string;
  userId: string; // the party being OFFERED the draw
  tableId: number;
  requester: string;
};

/** drawRejectMsgSchema (schema.js L401) */
export type DrawRejectMessage = {
  requestId: string;
  type: string;
  userId: string;
  tableId: number;
  responsee: string; // (sic)
};

/** gameEndResMsgSchema (schema.js L409-431) */
export type GameEndResponse = {
  requestId: string;
  type: string;
  endType: string;
  userId: string; // NOT the winner
  tableId: number;
  gameState: string; // final FEN
  winner: string; // '00' draw, '0' first-move abort
  pgn: string;
  moves: string[]; // FULL list
  whitePiecesCaptured: string[];
  blackPiecesCaptured: string[];
  opponent: string;
  playerMoveTimer: number;
  moveTimeIncrement: number;
  white: string;
  winner_rating: number;
  opponent_rating: number;
  winner_rating_delta: string; // signed FLOAT string
  opponent_rating_delta: string;
  winner_gameId: string;
  opponent_gameId: string;
};

/** emptyGameStateSchema (schema.js L75-80) */
export type EmptyGameStateMessage = {
  requestId: string;
  type: string;
  message: string;
};

/** tournamentOpeningPrepEvalPendingResMsgSchema */
export type EvalPendingMessage = {
  requestId: string;
  type: string;
  tableId: number;
  message?: string;
};

/**
 * challengeGameMsgSchema (schema.js L449-461) — the ONE schema all five
 * `challenge_*` types ride, outbound and inbound. Every field must be present on
 * the wire: schemapack encodes positionally, so a missing key decodes the whole
 * packet as null on the other side (cc-game-service says so in as many words,
 * joinLeaveProcessor L114-117).
 *
 * `opponent` is `<userId>:<username>`; the server splits it and ignores any
 * further `:` segment (joinLeaveProcessor L95-101). `white` is a BARE user id or
 * '' — it is stored as the table's `tossWinner` verbatim and a non-empty value
 * turns the toss off (cc-redis-client allocateSeat.lua L19-29, L119).
 */
export type ChallengeGameMessage = {
  requestId: string;
  type: string;
  variantId: number;
  userId: string;
  opponent: string;
  playerMoveTimer: number; // ms
  moveTimeIncrement: number; // ms
  gameState: string; // start FEN
  white: string;
  black: string;
  gamePlayMode: string;
};

/** onlinePlayersSearchMsgSchema (schema.js L479-484) */
export type OnlinePlayersSearchMessage = {
  requestId: string;
  pattern: string;
  type: string;
  userId: string;
};

/** onlinePlayersResultMsgSchema (schema.js L486-490) */
export type OnlinePlayersResultMessage = {
  requestId: string;
  type: string;
  /** JSON string of `{[username]: userId}` */
  players: string;
};

/** arenaEnvelopeSchema (schema.js L679-686) */
export type ArenaEnvelope = {
  requestId: string;
  type: string;
  arenaId: string;
  tableId: string;
  payload: string; // JSON
};
